/*
 * Filter drawer behavior — collections + search pages.
 *
 * - Opens by toggling `js-filter-drawer-open` on <body> (CSS handles the slide).
 * - Holds a "draft" state separate from the URL. Ticking a pill or
 *   changing sort updates the draft; the products grid does not update
 *   until the user clicks View Results.
 * - Debounced background fetch of the filtered URL parses the result
 *   count from the rendered HTML and updates the View Results button.
 * - View Results commits the draft to the URL (full page navigation).
 * - Clear filters resets the draft (sort untouched), button counter
 *   falls back to the base result count.
 */
(function () {
  const BODY_OPEN_CLASS = 'js-filter-drawer-open';
  const COUNT_DEBOUNCE_MS = 200;

  const drawer = document.querySelector('[data-filter-drawer]');
  if (!drawer) return;

  const form = drawer.querySelector('[data-filter-drawer-form]');
  const countSlot = drawer.querySelector('[data-filter-drawer-count]');
  const closeButtons = document.querySelectorAll('[data-filter-drawer-close]');
  const openButtons = document.querySelectorAll('[data-filter-drawer-open]');
  const clearButton = drawer.querySelector('[data-filter-drawer-clear]');

  const baseCount = parseInt(drawer.dataset.resultsBaseCount || '0', 10);

  // --- Open / close ----------------------------------------------------
  // No auto-commit on close — selections are applied live via
  // updateResults() as the user changes filters, so the URL always
  // reflects the current form state. Closing just hides the drawer.
  function openDrawer() {
    document.body.classList.add(BODY_OPEN_CLASS);
  }
  function closeDrawer() {
    document.body.classList.remove(BODY_OPEN_CLASS);
  }

  // Back / forward: AJAX swaps push history with new URLs but no DOM
  // mutation fires on popstate. We hydrate the form (checkboxes +
  // price range), re-sync the sort chip from the URL, then call
  // updateResults() — the same AJAX path filter changes already use.
  // Defined after the helpers below; declared here as a forward note.
  window.addEventListener('popstate', () => {
    hydrateFormFromURL();
    syncSortStateFromURL();
    lastQueryString = null; // force updateResults to refetch
    // The browser restores scroll for history entries itself — don't
    // fight it with the post-swap scroll correction.
    updateResults({ correctScroll: false });
  });

  // Maps for sort_by URL value ↔ chip state. Used by both the popover
  // click handlers (key+direction → URL) and the popstate hydrator
  // (URL → chip label + pill + direction toggle).
  const SORT_LABELS = {
    featured: 'Featured',
    newest: 'Newest',
    price: 'Price',
    weight: 'Weight',
    relevance: 'Most relevant',
  };
  function parseSortBy(sortBy) {
    if (!sortBy || sortBy === 'manual') return { key: 'featured', direction: null };
    if (sortBy === 'relevance') return { key: 'relevance', direction: null };
    const direction = sortBy.endsWith('-ascending') ? 'asc' : 'desc';
    if (sortBy.startsWith('created-')) return { key: 'newest', direction };
    if (sortBy.startsWith('price-')) return { key: 'price', direction };
    if (sortBy.startsWith('weight-')) return { key: 'weight', direction };
    return { key: 'featured', direction: null }; // unknown sort_by
  }

  // --- Client-side weight sort ----------------------------------------
  // Reorders the rendered grid by data-product-weight (variant.weight,
  // in grams) when sort_by is weight-ascending / weight-descending.
  //
  // This is intentionally client-side, not server-side. Shopify Liquid
  // can't paginate a sorted array — `paginate` only accepts native
  // collection objects, not the result of `collection.products | sort`.
  // So server-side sort + pagination together is impossible for any
  // data source (variant.weight OR a metafield). The workaround:
  // paginate the unsorted collection by 250 (Shopify's max), let JS
  // sort within the 250.
  //
  // CONSEQUENCE: collections > 250 products only sort their first 250.
  // We hide the Weight sort pill entirely on those collections (via
  // show_weight=false from main-collection-product-grid.liquid) so the
  // user never sees a partial-sort result.
  function applyWeightSort() {
    const sortBy = new URLSearchParams(window.location.search).get('sort_by') || '';
    if (!sortBy.startsWith('weight-')) return;

    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const ascending = sortBy.endsWith('-ascending');
    const items = Array.from(grid.children);
    items.sort((a, b) => {
      const wa = parseFloat(a.querySelector('[data-product-weight]')?.dataset.productWeight || '0');
      const wb = parseFloat(b.querySelector('[data-product-weight]')?.dataset.productWeight || '0');
      return ascending ? wa - wb : wb - wa;
    });
    items.forEach((item) => grid.appendChild(item));
  }

  applyWeightSort();
  document.addEventListener('productGridUpdated', applyWeightSort);

  openButtons.forEach((btn) => btn.addEventListener('click', openDrawer));
  closeButtons.forEach((btn) => btn.addEventListener('click', closeDrawer));

  // --- Sort popover ----------------------------------------------------
  // Anchored beneath the sort chip. Tap an option → URL gains/updates
  // sort_by while preserving existing filter params, and the page
  // reloads with the new sort applied.
  const sortRoot = document.querySelector('[data-sort-popover-root]');
  if (sortRoot) {
    const sortTrigger = sortRoot.querySelector('[data-sort-popover-trigger]');
    const sortPopover = sortRoot.querySelector('[data-sort-popover]');

    const setSortOpen = (open) => {
      sortPopover.dataset.open = open ? 'true' : 'false';
      sortTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    };

    sortTrigger.addEventListener('click', (event) => {
      event.stopPropagation();
      setSortOpen(sortPopover.dataset.open !== 'true');
    });

    document.addEventListener('click', (event) => {
      if (sortPopover.dataset.open === 'true' && !sortRoot.contains(event.target)) {
        setSortOpen(false);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && sortPopover.dataset.open === 'true') {
        setSortOpen(false);
        sortTrigger.focus();
      }
    });

    // Map a logical sort key + direction → Shopify's sort_by URL value.
    // Featured and relevance have no direction.
    const sortValueFor = (key, direction) => {
      if (key === 'featured') return 'manual';
      if (key === 'relevance') return 'relevance';
      const axis = key === 'newest' ? 'created' : key; // newest → created
      return `${axis}-${direction === 'asc' ? 'ascending' : 'descending'}`;
    };

    // Navigate with a new sort_by, preserving filter params + resetting
    // pagination so the user doesn't land on a page that may not exist
    // under the new ordering.
    const applySortValue = (value) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');
      window.location = url.toString();
    };

    // Pill click: pick a new sort key with its default direction (the
    // inline direction toggle next to the chip handles asc ↔ desc).
    sortPopover.querySelectorAll('[data-sort-key]').forEach((pill) => {
      pill.addEventListener('click', () => {
        const key = pill.dataset.sortKey;
        const direction = pill.dataset.defaultDirection;
        applySortValue(sortValueFor(key, direction));
      });
    });

    // Direction toggle next to the chip: flip asc ↔ desc without
    // opening the popover.
    const directionToggle = sortRoot.querySelector('[data-sort-direction-toggle]');
    if (directionToggle) {
      directionToggle.addEventListener('click', (event) => {
        event.stopPropagation();
        const activePill = sortPopover.querySelector('[data-sort-key][data-active="true"]');
        if (!activePill) return;
        const currentDirection = directionToggle.dataset.direction || 'desc';
        const nextDirection = currentDirection === 'asc' ? 'desc' : 'asc';
        applySortValue(sortValueFor(activePill.dataset.sortKey, nextDirection));
      });
    }
  }

  // --- Sticky toolbar "tucks into header" -----------------------------
  // The collection toolbar is position:sticky just below the sticky
  // masthead. Its sticky `top` is calc(--announcement-bar-height +
  // --header-height) in CSS, and each of those is published by the
  // element it describes: the announcement bar and the header both keep
  // theirs honest with a ResizeObserver (sections/announcement-bar.liquid,
  // sections/header.liquid). Nothing here measures the header any more —
  // until 2026-09-11 this file read the header's rect on window resize,
  // which missed every change that fires no resize event. The published
  // values are read only for the stuck detector's root margin below.
  const rootPx = (name, fallback) => {
    const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
    return Number.isFinite(v) ? v : fallback;
  };
  function getCombinedOffset() {
    return rootPx('--announcement-bar-height', 0) + rootPx('--header-height', 60);
  }

  const toolbar = document.querySelector('[data-collection-toolbar]');
  if (toolbar) {
    // Stuck detection: a zero-height sentinel sits in flow just above
    // the toolbar at its natural position. An IntersectionObserver
    // with a negative top rootMargin watches it cross the masthead's
    // lower edge — when the sentinel scrolls above that line, the
    // toolbar is stuck.
    //
    // Earlier attempts used scrollY > computed-threshold, where the
    // threshold came from walking the toolbar's offsetParent chain
    // summing offsetTop. iOS Safari corrupts offsetTop's result when
    // a resize fires while the toolbar is in its sticky-pinned
    // state (URL bar transitions during scroll trigger this). The
    // threshold would jump to a nonsense value and is-stuck would
    // never toggle correctly again. The sentinel + IO approach has
    // no offsetTop calculation, so it's immune.
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    // display:block needs to be explicit — something in the cascade
    // was hiding the sentinel with display:none (likely a Dawn-side
    // *:empty or similar reset). Without display, the sentinel had
    // no layout box and IO permanently reported it as "above viewport"
    // (rect.top = 0), so chips were stuck-styled at all times.
    sentinel.style.cssText = 'display:block;height:1px;width:100%;margin:0;padding:0;pointer-events:none;';
    toolbar.parentNode.insertBefore(sentinel, toolbar);

    let stickObserver = null;
    let stickOffset = null;
    function refreshStickObserver() {
      if (stickObserver) stickObserver.disconnect();
      const offset = (stickOffset = Math.round(getCombinedOffset()));
      stickObserver = new IntersectionObserver(
        ([entry]) => {
          // Sentinel above the viewport (top edge accounting for
          // masthead height) → toolbar is stuck. Sentinel intersects
          // → toolbar is in flow.
          const stuck = !entry.isIntersecting && entry.boundingClientRect.top < offset;
          toolbar.classList.toggle('is-stuck', stuck);
        },
        { rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 }
      );
      stickObserver.observe(sentinel);
    }
    refreshStickObserver();

    // The offset changes only when one of the two published variables
    // does — the bar dismissed or re-measured, the header re-measured as
    // a breakpoint is crossed — and every one of those is an inline style
    // write on <html>. Watch that attribute, not the window: iOS fires no
    // resize for its own chrome, and a resize that moves neither variable
    // has nothing to tell the observer.
    let syncRaf = 0;
    new MutationObserver(() => {
      cancelAnimationFrame(syncRaf);
      syncRaf = requestAnimationFrame(() => {
        if (Math.round(getCombinedOffset()) !== stickOffset) refreshStickObserver();
      });
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
  }

  // ESC to close
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.body.classList.contains(BODY_OPEN_CLASS)) {
      closeDrawer();
    }
  });

  // --- Live count -------------------------------------------------------
  // Build the URL the form would submit to, fetch it, parse the count,
  // update the View Results button label.
  let countAbort = null;
  let lastQueryString = null;

  function buildQueryString() {
    const params = new URLSearchParams();
    // Context the page carries outside the filter controls — on /search
    // that's q, type and options[prefix]. Set first so a filter input
    // could never be shadowed by one, and set (not append) so a rebuild
    // can't duplicate them.
    form.querySelectorAll('input[type="hidden"][name]').forEach((input) => {
      params.set(input.name, input.value);
    });
    // Sort first (keeps URL deterministic). The sort popover writes
    // sort_by directly to the URL — it's not a form input — so we
    // preserve whatever's currently in the URL. A form input named
    // sort_by (if one ever existed) would still take precedence.
    const urlSort = new URLSearchParams(window.location.search).get('sort_by');
    if (urlSort) params.set('sort_by', urlSort);
    const sortInput = form.querySelector('input[name="sort_by"]:checked');
    if (sortInput && sortInput.value) params.set('sort_by', sortInput.value);

    // Filters
    form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
      if (input.name) params.append(input.name, input.value);
    });

    // Price range — include only when at least one handle has been
    // moved off its bound. A slider still at full bounds is a no-op
    // filter and would otherwise add a spurious "Price" active chip.
    drawer.querySelectorAll('[data-price-range]').forEach((range) => {
      const minInput = range.querySelector('[data-range-min-input]');
      const maxInput = range.querySelector('[data-range-max-input]');
      const minAtBound = minInput && parseFloat(minInput.value) === parseFloat(minInput.min);
      const maxAtBound = maxInput && parseFloat(maxInput.value) === parseFloat(maxInput.max);
      if (minAtBound && maxAtBound) return;
      range.querySelectorAll('input[name]').forEach((input) => {
        if (input.value !== '') params.set(input.name, input.value);
      });
    });

    return params.toString();
  }

  // Single <link rel="prefetch"> reused across count updates. Tells the
  // browser "the user is about to navigate here" so the document is
  // pre-cached at navigation priority — the View Results click then
  // renders instantly because the response is already in cache.
  let prefetchLink = null;
  function prefetchDestination(url) {
    if (!prefetchLink) {
      prefetchLink = document.createElement('link');
      prefetchLink.rel = 'prefetch';
      prefetchLink.as = 'document';
      document.head.appendChild(prefetchLink);
    }
    prefetchLink.href = url;
  }

  function updateResults(opts) {
    const correctScroll = !opts || opts.correctScroll !== false;
    const query = buildQueryString();
    if (query === lastQueryString) return;
    lastQueryString = query;

    const baseUrl = drawer.dataset.resultsUrl || window.location.pathname;
    const url = query ? `${baseUrl}?${query}` : baseUrl;

    // Push the new URL immediately so the address bar always reflects
    // the currently-selected filters — visible product grid may lag a
    // moment behind while the fetch resolves.
    if (window.location.pathname + window.location.search !== url) {
      window.history.pushState({}, '', url);
    }

    // Warm the browser cache as a fallback for hard navigation.
    prefetchDestination(url);

    if (countAbort) countAbort.abort();
    countAbort = new AbortController();

    fetch(url, { signal: countAbort.signal })
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // 1) Count chip — prefer server-rendered paginate.items via
        //    data-total-count; fall back to counting visible children.
        const newGrid = doc.querySelector('#product-grid');
        if (countSlot) {
          let count = 0;
          if (newGrid) {
            if (newGrid.dataset.totalCount) {
              count = parseInt(newGrid.dataset.totalCount, 10) || 0;
            } else {
              count = newGrid.querySelectorAll(':scope > li, :scope > .grid__item').length;
            }
          }
          countSlot.textContent = `(${count})`;
        }

        // 2) Swap the product grid contents in place. We swap inside
        //    #CollectionProductGrid (preserving its outer element +
        //    any listeners attached to it) rather than replacing it.
        const currentGridContainer = document.getElementById('CollectionProductGrid');
        const newGridContainer = doc.getElementById('CollectionProductGrid');
        if (currentGridContainer && newGridContainer) {
          currentGridContainer.innerHTML = newGridContainer.innerHTML;
        }

        // 2b) A fresh set of results starts from the top of the page —
        //     without this, the browser clamps the old (possibly
        //     infinite-scrolled) offset to the new shorter document
        //     and dumps the viewport at the footer. behavior:'instant'
        //     is load-bearing: html{scroll-behavior:smooth} would
        //     animate a plain call (see the mobile-menu glide fix).
        if (correctScroll) {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          // The overlay scroll lock (global.js) pins the page while the
          // drawer is open; this tells the pin to adopt the new position
          // instead of snapping the correction back.
          document.dispatchEvent(new CustomEvent('co:overlay-repin'));
        }

        // 3) Sync the active-filter chip row in the toolbar so the
        //    chips reflect the new selection live. Handle all four
        //    cases: chips present in both / new only / current only /
        //    neither.
        const newChips = doc.querySelector('.collection-toolbar__active-filters');
        const currentChips = document.querySelector('.collection-toolbar__active-filters');
        const controls = document.querySelector('.collection-toolbar__controls');
        if (newChips && currentChips) {
          currentChips.innerHTML = newChips.innerHTML;
        } else if (newChips && !currentChips && controls) {
          controls.appendChild(newChips.cloneNode(true));
        } else if (!newChips && currentChips) {
          currentChips.remove();
        }

        // 4) Re-initialise the infinite-scroll component on the new
        //    grid (infinite-scroll.js listens for this event).
        document.dispatchEvent(new CustomEvent('infiniteScrollReset'));
        document.dispatchEvent(new CustomEvent('productGridUpdated'));
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('Filter update failed', error);
        }
      });
  }

  // Use the theme's global debounce helper (assets/global.js).
  const scheduleCountUpdate = debounce(updateResults, COUNT_DEBOUNCE_MS);

  form.addEventListener('change', scheduleCountUpdate);
  form.addEventListener('input', (event) => {
    if (event.target.matches('input[type="number"], input[type="range"]')) scheduleCountUpdate();
  });

  // --- Popstate hydration ---------------------------------------------
  // hydrateFormFromURL is the inverse of buildQueryString: it reads
  // window.location.search and writes it back into the form's
  // checkboxes + price range inputs. For the slider we synthesise an
  // 'input' event on minInput so the existing slider handler redraws
  // the fill + value labels (no need to duplicate that logic here).
  function hydrateFormFromURL() {
    const params = new URLSearchParams(window.location.search);

    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (!input.name) return;
      input.checked = params.getAll(input.name).includes(input.value);
    });

    drawer.querySelectorAll('[data-price-range]').forEach((range) => {
      const minInput = range.querySelector('[data-range-min-input]');
      const maxInput = range.querySelector('[data-range-max-input]');
      if (minInput) {
        const v = params.get(minInput.name);
        minInput.value = v !== null ? v : minInput.min;
      }
      if (maxInput) {
        const v = params.get(maxInput.name);
        maxInput.value = v !== null ? v : maxInput.max;
      }
      // Triggers the slider's update() → fill + labels redraw, and the
      // form's 'input' listener → debounced no-op (lastQueryString will
      // match by then, see updateResults early-return).
      minInput?.dispatchEvent(new Event('input', { bubbles: true }));
    });

    syncSelectionState();
  }

  // syncSortStateFromURL — reads sort_by from URL, updates chip label,
  // active pill state, and direction toggle. Mirrors the Liquid setup
  // in collection-sort-popover.liquid for the popstate case.
  function syncSortStateFromURL() {
    const sortPopoverRoot = document.querySelector('[data-sort-popover-root]');
    if (!sortPopoverRoot) return;
    const popover = sortPopoverRoot.querySelector('[data-sort-popover]');
    const trigger = sortPopoverRoot.querySelector('[data-sort-popover-trigger]');
    if (!popover || !trigger) return;

    const sortBy = new URLSearchParams(window.location.search).get('sort_by') || '';
    const { key, direction } = parseSortBy(sortBy);

    // Chip label — the first <span> inside the trigger holds the text
    // (the second <span> is the ▾ caret).
    const chipLabel = trigger.querySelector('span:not(.collection-toolbar__sort-chip-caret)');
    if (chipLabel) chipLabel.textContent = SORT_LABELS[key] || 'Sort';

    popover.querySelectorAll('[data-sort-key]').forEach((pill) => {
      const isActive = pill.dataset.sortKey === key;
      if (isActive) {
        pill.dataset.active = 'true';
        pill.setAttribute('aria-checked', 'true');
        if (direction) pill.dataset.direction = direction;
        else delete pill.dataset.direction;
      } else {
        delete pill.dataset.active;
        pill.setAttribute('aria-checked', 'false');
        delete pill.dataset.direction;
      }
    });

    const directionToggle = sortPopoverRoot.querySelector('[data-sort-direction-toggle]');
    if (directionToggle) {
      directionToggle.hidden = !direction;
      if (direction) directionToggle.dataset.direction = direction;
    }
  }

  // --- Selection state — View Results lifts to neon -------------------
  // Toggle .has-selection on the drawer when the form has any meaningful
  // selection (a checked pill OR a price range that's been moved off
  // its bounds). The CSS uses this to repaint View Results in neon, so
  // the action button only stands out *after* the user has done
  // something to apply.
  function syncSelectionState() {
    let hasSelection = false;
    if (form.querySelector('input[type="checkbox"]:checked')) {
      hasSelection = true;
    } else {
      drawer.querySelectorAll('[data-price-range]').forEach((range) => {
        if (hasSelection) return;
        const minInput = range.querySelector('[data-range-min-input]');
        const maxInput = range.querySelector('[data-range-max-input]');
        if (minInput && parseFloat(minInput.value) !== parseFloat(minInput.min)) hasSelection = true;
        if (maxInput && parseFloat(maxInput.value) !== parseFloat(maxInput.max)) hasSelection = true;
      });
    }
    drawer.classList.toggle('has-selection', hasSelection);
  }
  form.addEventListener('change', syncSelectionState);
  // For sliders, `change` only fires on commit (drag end). Also listen
  // for `input` on range thumbs specifically so the View Results
  // button lights up during drag — gated to range inputs so checkbox
  // keystrokes don't re-walk the whole form unnecessarily.
  form.addEventListener('input', (event) => {
    if (event.target.matches('[data-range-min-input], [data-range-max-input]')) {
      syncSelectionState();
    }
  });
  syncSelectionState();

  // --- Range slider ----------------------------------------------------
  // Each range filter widget is a [data-price-range] container with two
  // <input type="range"> children (min + max) and a track fill. As the
  // user drags either thumb we re-render the fill position and the two
  // numeric labels. Min cannot exceed max (and vice versa).
  //
  // The `data-price-range` attribute is kept for backward compat with
  // the original price-only implementation; the slider is now generic
  // and serves the Price filter AND any metafield range filter (Weight)
  // configured in Search & Discovery. The Liquid snippet stamps
  // `data-range-format` on the container (currency / grams / number)
  // and we pick the matching formatter below.
  const rangeFormatters = {
    currency: (value) => `$${Math.round(value)}`,
    grams: (value) => `${Math.round(value)}g`,
    number: (value) => `${Math.round(value)}`,
  };

  drawer.querySelectorAll('[data-price-range]').forEach((range) => {
    const minInput = range.querySelector('[data-range-min-input]');
    const maxInput = range.querySelector('[data-range-max-input]');
    const fill = range.querySelector('[data-range-fill]');
    // Floating value labels live INSIDE the range container so they
    // can be absolute-positioned relative to the track.
    const minLabel = range.querySelector('[data-range-min-label]');
    const maxLabel = range.querySelector('[data-range-max-label]');
    if (!minInput || !maxInput) return;

    const min = parseFloat(range.dataset.rangeMin || '0');
    const max = parseFloat(range.dataset.rangeMax || '0');
    if (!max) return;

    const format = rangeFormatters[range.dataset.rangeFormat] || rangeFormatters.currency;

    function update() {
      let lo = parseFloat(minInput.value) || min;
      let hi = parseFloat(maxInput.value) || max;
      // Don't let the handles cross
      if (lo > hi) {
        if (this === minInput) lo = hi;
        else hi = lo;
        minInput.value = lo;
        maxInput.value = hi;
      }
      const pctLo = ((lo - min) / (max - min)) * 100;
      const pctHi = ((hi - min) / (max - min)) * 100;
      if (fill) {
        fill.style.left = pctLo + '%';
        fill.style.right = (100 - pctHi) + '%';
      }
      // Chip slides without overflowing the container: translateX
      // matches the negative percentage so the chip's left edge sits
      // at 0% when thumb at min, and right edge at 100% when at max.
      // The caret stays pointing at the thumb via --triangle-left,
      // which moves through the chip from 0% (at min) to 100% (at max).
      if (minLabel) {
        minLabel.textContent = format(lo);
        minLabel.style.left = pctLo + '%';
        minLabel.style.transform = `translateX(-${pctLo}%)`;
        minLabel.style.setProperty('--triangle-left', pctLo + '%');
      }
      if (maxLabel) {
        maxLabel.textContent = format(hi);
        maxLabel.style.left = pctHi + '%';
        maxLabel.style.transform = `translateX(-${pctHi}%)`;
        maxLabel.style.setProperty('--triangle-left', pctHi + '%');
      }
    }

    minInput.addEventListener('input', update);
    maxInput.addEventListener('input', update);
    update();
  });

  // --- Form submit ----------------------------------------------------
  // Live updates apply selections to the URL as the user changes
  // filters, so by the time View Results is clicked the URL already
  // reflects the current state. The submit handler just closes the
  // drawer — no navigation needed.
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    // Flush any pending debounced update so the URL is guaranteed to
    // reflect the latest selection before closing.
    updateResults();
    closeDrawer();
  });

  // --- Clear filters ---------------------------------------------------
  if (clearButton) {
    clearButton.addEventListener('click', () => {
      // Uncheck all checkboxes; reset price ranges to bounds. Sort stays.
      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.checked = false;
      });
      form.querySelectorAll('input[type="number"]').forEach((input) => {
        input.value = '';
      });
      drawer.querySelectorAll('[data-price-range]').forEach((range) => {
        const minInput = range.querySelector('[data-range-min-input]');
        const maxInput = range.querySelector('[data-range-max-input]');
        if (minInput) minInput.value = minInput.min;
        if (maxInput) maxInput.value = maxInput.max;
        minInput?.dispatchEvent(new Event('input', { bubbles: true }));
      });
      if (countSlot) countSlot.textContent = `(${baseCount})`;
      lastQueryString = null;
    });
  }
})();
